/**
 * SignOffSection — the gated, two-tier CAP approval and closure workflow.
 *
 *   1. Business approval    → checkpoint 'business_plan'   (the business/owner signs
 *                             off that the root cause + remediation plan is right)
 *   2. Compliance approval  → checkpoint 'compliance_plan' (final approver; unlocks
 *                             after business approval — the CAP is now cleared to work)
 *   3. Validation           → checkpoint 'validation'      (the remediation worked,
 *                             with evidence; unlocks after compliance approval)
 *   4. Close out            → checkpoint 'closure'         (requires a mandatory
 *                             rationale and disclosed attachments; unlocks after
 *                             validation, and only if the CAP is defensibly complete)
 *
 * The order is enforced on the server too; the UI mirrors it so the flow is legible.
 */

import React, { useState } from 'react';
import SectionCard from './SectionCard';

const API = process.env.REACT_APP_API_URL;

function isComplianceApprover(user) {
  if (!user) return false;
  if (user.app_roles && user.app_roles.cap === 'CAP Approver') return true;
  return ['superadmin', 'admin'].includes(user.role);
}
// The "business" tier — the owner/team working the CAP. Any CAP role qualifies,
// as does a compliance approver.
function isBusinessApprover(user) {
  if (!user) return false;
  if (user.app_roles && user.app_roles.cap) return true;
  return isComplianceApprover(user);
}

function fmtDate(ts) { try { return ts ? new Date(ts).toLocaleDateString() : ''; } catch { return ''; } }

function CheckpointRow({ cp, latest, canAct, locked, lockedReason, requireNote, attachments, onSubmit, busy }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [error, setError] = useState(null);

  const decide = async (decision) => {
    setError(null);
    if (decision === 'approved' && requireNote && !note.trim()) { setError('A closure rationale is required.'); return; }
    try {
      await onSubmit(cp.key, decision, note.trim() || null);
      setOpen(false); setNote('');
    } catch (err) { setError(err.message); }
  };

  const approved = latest?.decision === 'approved';
  return (
    <li className="py-3.5 first:pt-0 last:pb-0">
      <div className="flex items-start gap-3">
        <span className={`mt-1 h-5 w-5 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${approved ? 'bg-emerald-100 text-emerald-700' : latest?.decision === 'rejected' ? 'bg-red-100 text-red-700' : locked ? 'bg-slate-100 text-slate-400' : 'bg-slate-100 text-slate-500'}`}>
          {approved ? '✓' : latest?.decision === 'rejected' ? '✗' : cp.n}
        </span>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium ${locked ? 'text-slate-400' : 'text-slate-800'}`}>{cp.label}</p>
          <p className="text-[12px] text-slate-500">{cp.hint}</p>
          {latest && (
            <p className="text-[12px] mt-1">
              {approved
                ? <span className="text-emerald-700">Approved by {latest.decider_name || 'an approver'} on {fmtDate(latest.decided_at)}</span>
                : <span className="text-red-700">Rejected by {latest.decider_name || 'an approver'} on {fmtDate(latest.decided_at)}</span>}
              {latest.note && <span className="text-slate-500"> — {latest.note}</span>}
            </p>
          )}
          {locked && !approved && <p className="text-[11px] text-slate-400 mt-0.5 italic">{lockedReason}</p>}
        </div>
        {!open && !approved && (
          <button type="button" onClick={() => setOpen(true)} disabled={!canAct || locked || busy}
            className="px-2.5 py-1 rounded-md text-[12px] text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            title={!canAct ? 'You do not have rights for this step.' : locked ? lockedReason : ''}>
            Sign off
          </button>
        )}
      </div>

      {open && (
        <div className="mt-2.5 ml-8 rounded-lg ring-1 ring-slate-200 bg-slate-50 p-3 space-y-2.5">
          {/* Attachment disclosure at closure */}
          {cp.key === 'closure' && (
            <div className="rounded-md bg-white ring-1 ring-slate-200 p-2.5">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Attachments on file ({attachments.length})</p>
              {attachments.length === 0 ? (
                <p className="text-[12px] text-red-600">No evidence is attached — a CAP cannot be closed without documented proof.</p>
              ) : (
                <ul className="space-y-0.5">
                  {attachments.slice(0, 12).map((e) => (
                    <li key={e.id} className="text-[12px] text-slate-600">• {e.file_name || e.description || (e.kind === 'link' ? e.link_url : e.note_body) || 'Evidence'}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <textarea
            value={note} onChange={e => setNote(e.target.value)}
            placeholder={requireNote ? 'Closure rationale — why is this CAP defensibly complete? (required)' : 'Add a short note (optional)'}
            rows={requireNote ? 3 : 2}
            className="w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
          />
          {error && <p className="text-[12px] text-red-700">{error}</p>}
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => decide('approved')} disabled={busy}
              className="px-3 py-1.5 rounded-md bg-emerald-700 text-white text-[12px] font-medium hover:bg-emerald-800 disabled:opacity-50">
              {busy ? 'Saving…' : 'Approve'}
            </button>
            <button type="button" onClick={() => decide('rejected')} disabled={busy}
              className="px-3 py-1.5 rounded-md ring-1 ring-red-200 text-red-700 text-[12px] font-medium hover:bg-red-50 disabled:opacity-50">
              Reject
            </button>
            <button type="button" onClick={() => { setOpen(false); setNote(''); setError(null); }}
              className="px-3 py-1.5 rounded-md text-[12px] text-slate-600 hover:bg-slate-100">Cancel</button>
          </div>
        </div>
      )}
    </li>
  );
}

export default function SignOffSection({ data, onRefresh, user }) {
  const cap = data.cap;
  const approvals = data.approvals || [];
  const attachments = data.evidence || [];
  const canCompliance = isComplianceApprover(user);
  const canBusiness = isBusinessApprover(user);
  const [busy, setBusy] = useState(false);

  const latestByCheckpoint = {};
  for (const a of approvals) {
    const prev = latestByCheckpoint[a.checkpoint];
    if (!prev || new Date(a.decided_at) > new Date(prev.decided_at)) latestByCheckpoint[a.checkpoint] = a;
  }
  const approvedOf = (k) => latestByCheckpoint[k]?.decision === 'approved';

  const steps = [
    { key: 'business_plan',   n: 1, label: 'Business approval',        hint: 'The business owner signs off that the root cause and remediation plan are right.', canAct: canBusiness,   locked: false,                        lockedReason: '' },
    { key: 'compliance_plan', n: 2, label: 'Compliance approval (final)', hint: 'Compliance gives final approval. The CAP is then cleared to be worked.',        canAct: canCompliance, locked: !approvedOf('business_plan'), lockedReason: 'Waiting on business approval.' },
    { key: 'validation',      n: 3, label: 'Validation',              hint: 'Confirm the remediation worked, with evidence.',                                    canAct: canCompliance, locked: !approvedOf('compliance_plan'), lockedReason: 'Waiting on compliance approval.' },
    { key: 'closure',         n: 4, label: 'Close out',               hint: 'Requires a closure rationale and disclosed attachments.',                            canAct: canCompliance, locked: !approvedOf('validation'),    lockedReason: 'Waiting on validation.', requireNote: true },
  ];

  const submitDecision = async (checkpoint, decision, note) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/pcap/caps/${cap.id}/approvals`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ checkpoint, decision, note }),
      });
      const d = await res.json();
      if (!res.ok || !d.success) {
        const extra = d.reasons?.length ? ` (${d.reasons.join('; ')})` : '';
        throw new Error((d.message || 'Could not save') + extra);
      }
      onRefresh();
    } finally { setBusy(false); }
  };

  return (
    <SectionCard
      title="Approval & closure"
      hint="Business approves the plan first, then compliance. After validation, the CAP is closed with a rationale and disclosed attachments."
    >
      {!canBusiness && (
        <p className="text-[12px] text-slate-500 italic mb-2">You can read approval status here. Only approvers can sign off.</p>
      )}
      <ul className="divide-y divide-slate-100">
        {steps.map((cp) => (
          <CheckpointRow
            key={cp.key}
            cp={cp}
            latest={latestByCheckpoint[cp.key]}
            canAct={cp.canAct}
            locked={cp.locked}
            lockedReason={cp.lockedReason}
            requireNote={!!cp.requireNote}
            attachments={attachments}
            onSubmit={submitDecision}
            busy={busy}
          />
        ))}
      </ul>
    </SectionCard>
  );
}
